import { useCallback, useEffect, useMemo, useState } from "react";
import { getCsrfHeader } from "../lib/csrf";
import { readUserCache, writeUserCache } from "../lib/dashboardCache";
import { channelFromApi, type ChannelConnection } from "../types/channel";

export type ChannelsStatus = "loading" | "ready" | "error";

export type UseChannelsResult = {
  status: ChannelsStatus;
  channels: ReadonlyArray<ChannelConnection>;
  error: string | null;
  // Fire-and-forget refresh, used as the setup modals' onChanged callback so the
  // strip + hero reflect a newly connected channel.
  refetch: () => Promise<void>;
  // Record an access request for a non-self-serve channel. Resolves true on a
  // recorded request so the caller can flip the card to "Requested".
  request: (channel: string) => Promise<boolean>;
  // Withdraw a previously-recorded request. Resolves true once cancelled.
  cancelRequest: (channel: string) => Promise<boolean>;
};

const channelsResource = (tenantId: string | null): string =>
  `channels:${tenantId ?? "none"}`;

export const useChannels = (tenantId: string | null): UseChannelsResult => {
  const resource = channelsResource(tenantId);
  const cached = useMemo(
    () => readUserCache<ReadonlyArray<ChannelConnection>>(resource),
    [resource],
  );
  const [status, setStatus] = useState<ChannelsStatus>(() => {
    if (tenantId === null) return "ready";
    return cached !== null ? "ready" : "loading";
  });
  const [channels, setChannels] = useState<ReadonlyArray<ChannelConnection>>(
    cached ?? [],
  );
  const [error, setError] = useState<string | null>(null);

  const fetchChannels = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      if (tenantId === null) {
        setChannels([]);
        setStatus("ready");
        return;
      }
      const init: RequestInit = signal === undefined
        ? { credentials: "include" }
        : { credentials: "include", signal };
      try {
        const res = await fetch(`/api/channels/${encodeURIComponent(tenantId)}`, init);
        if (!res.ok) {
          throw new Error(`channels fetch failed (${res.status})`);
        }
        const raw = (await res.json()) as ReadonlyArray<
          Parameters<typeof channelFromApi>[0]
        >;
        const mapped = raw.map(channelFromApi);
        setChannels(mapped);
        setStatus("ready");
        setError(null);
        writeUserCache(channelsResource(tenantId), mapped);
      } catch (err: unknown) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setError(err instanceof Error ? err.message : String(err));
        setStatus("error");
      }
    },
    [tenantId],
  );

  useEffect(() => {
    const controller = new AbortController();
    setChannels(cached ?? []);
    setStatus(tenantId === null ? "ready" : cached !== null ? "ready" : "loading");
    setError(null);
    void fetchChannels(controller.signal);
    return () => controller.abort();
  }, [fetchChannels, cached, tenantId]);

  const refetch = useCallback((): Promise<void> => fetchChannels(), [fetchChannels]);

  const request = useCallback(
    async (channel: string): Promise<boolean> => {
      if (tenantId === null) return false;
      try {
        const res = await fetch(
          `/api/channels/${encodeURIComponent(tenantId)}/${encodeURIComponent(channel)}/request`,
          { method: "POST", credentials: "include", headers: { ...getCsrfHeader() } },
        );
        if (!res.ok) return false;
        const raw = (await res.json()) as Parameters<typeof channelFromApi>[0];
        const updated = channelFromApi(raw);
        setChannels((prev) => {
          const next = prev.slice();
          const idx = next.findIndex((c) => c.channel === updated.channel);
          if (idx === -1) next.push(updated);
          else next[idx] = updated;
          writeUserCache(channelsResource(tenantId), next);
          return next;
        });
        return true;
      } catch {
        return false;
      }
    },
    [tenantId],
  );

  const cancelRequest = useCallback(
    async (channel: string): Promise<boolean> => {
      if (tenantId === null) return false;
      try {
        const res = await fetch(
          `/api/channels/${encodeURIComponent(tenantId)}/${encodeURIComponent(channel)}/request`,
          { method: "DELETE", credentials: "include", headers: { ...getCsrfHeader() } },
        );
        if (!res.ok) return false;
        setChannels((prev) => {
          const next = prev.filter((c) => c.channel !== channel);
          writeUserCache(channelsResource(tenantId), next);
          return next;
        });
        return true;
      } catch {
        return false;
      }
    },
    [tenantId],
  );

  return { status, channels, error, refetch, request, cancelRequest };
};
